import api from './api';

const API_BASE = '/api/v2';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

/**
 * Event API Service
 * Handles all event-related API calls for the new voting/invitation system
 */

// ============================================
// EVENT CRUD
// ============================================

export const eventAPI = {
  /**
   * Get all events
   */
  getEvents: async () => {
    const response = await api.get(`${API_BASE}/events`);
    return response.data;
  },

  /**
   * Get event by ID with full details
   */
  getEvent: async (eventId) => {
    const response = await api.get(`${API_BASE}/events/${eventId}`);
    return response.data;
  },

  /**
   * Create new event (direct or planning)
   */
  createEvent: async (eventData) => {
    const response = await api.post(`${API_BASE}/events`, eventData);
    return response.data;
  },

  /**
   * Update event
   */
  updateEvent: async (eventId, eventData) => {
    const response = await api.put(`${API_BASE}/events/${eventId}`, eventData);
    return response.data;
  },

  /**
   * Delete event
   */
  deleteEvent: async (eventId) => {
    const response = await api.delete(`${API_BASE}/events/${eventId}`);
    return response.data;
  },

  // ============================================
  // INVITATIONS
  // ============================================

  /**
   * Get pending invitations for current user
   */
  getPendingInvitations: async () => {
    const response = await api.get(`${API_BASE}/invitations/pending`);
    return response.data;
  },

  /**
   * Respond to invitation
   */
  respondToInvitation: async (eventId, status) => {
    const response = await api.put(`${API_BASE}/events/${eventId}/invitation`, {
      status
    });
    return response.data;
  },

  // ============================================
  // VOTING
  // ============================================

  /**
   * Submit votes for a planning event
   * @param {string} eventId - Event ID
   * @param {Array} votes - Array of {option_id, rank}
   */
  submitVotes: async (eventId, votes) => {
    const response = await api.post(`${API_BASE}/events/${eventId}/vote`, {
      votes
    });
    return response.data;
  },

  // ============================================
  // COUNTER-PROPOSALS
  // ============================================

  /**
   * Add counter-proposal to planning event
   */
  addCounterProposal: async (eventId, optionType, optionValue) => {
    const response = await api.post(`${API_BASE}/events/${eventId}/counter-proposal`, {
      option_type: optionType,
      option_value: optionValue
    });
    return response.data;
  },

  /**
   * Approve or reject counter-proposal (creator only)
   */
  approveCounterProposal: async (eventId, optionId, approved) => {
    const response = await api.put(
      `${API_BASE}/events/${eventId}/counter-proposal/${optionId}/approve`,
      { approved }
    );
    return response.data;
  },

  // ============================================
  // FINALIZATION
  // ============================================

  /**
   * Finalize planning event (creator only)
   */
  finalizeEvent: async (eventId, datetimeOptionId, locationOptionId) => {
    const response = await api.post(`${API_BASE}/events/${eventId}/finalize`, {
      datetime_option_id: datetimeOptionId,
      location_option_id: locationOptionId
    });
    return response.data;
  },

  // ============================================
  // COMMENTS
  // ============================================

  /**
   * Get comments for event
   */
  getComments: async (eventId) => {
    const response = await api.get(`${API_BASE}/events/${eventId}/comments`);
    return response.data;
  },

  /**
   * Add comment to event
   */
  addComment: async (eventId, commentText) => {
    const response = await api.post(`${API_BASE}/events/${eventId}/comments`, {
      comment_text: commentText
    });
    return response.data;
  },

  // ============================================
  // FAMILY MEMBERS
  // ============================================

  /**
   * Get family members for invitation selection
   */
  getFamilyMembers: async () => {
    const response = await api.get(`${API_BASE}/family-members`);
    return response.data;
  },

  // ============================================
  // EVENT IMAGES
  // ============================================

  /**
   * Upload images for an event
   */
  uploadEventImages: async (eventId, formData) => {
    const response = await api.post(`${API_BASE}/events/${eventId}/images`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  /**
   * Get images for an event
   */
  getEventImages: async (eventId) => {
    const response = await api.get(`${API_BASE}/events/${eventId}/images`);
    return response.data;
  },

  /**
   * Get image URL
   */
  getImageUrl: (filename) => {
    return `${API_URL}${API_BASE}/uploads/event_images/${filename}`;
  },

  /**
   * Update image caption
   */
  updateImageCaption: async (eventId, imageId, caption) => {
    const response = await api.patch(
      `${API_BASE}/events/${eventId}/images/${imageId}`,
      { caption }
    );
    return response.data;
  },

  // ============================================
  // EVENT BLOG POSTS
  // ============================================

  /**
   * Create blog post for an event
   */
  createBlogPost: async (eventId, content) => {
    const response = await api.post(`${API_BASE}/events/${eventId}/blog-posts`, {
      content
    });
    return response.data;
  },

  /**
   * Get blog posts for an event
   */
  getBlogPosts: async (eventId) => {
    const response = await api.get(`${API_BASE}/events/${eventId}/blog-posts`);
    return response.data;
  },

  // ============================================
  // CALENDAR FEED
  // ============================================

  /**
   * Get or create calendar feed token for current user
   */
  getCalendarFeedToken: async () => {
    const response = await api.get(`${API_BASE}/users/me/calendar-feed/token`);
    return response.data;
  },

  /**
   * Generate new calendar feed token (regenerate)
   */
  generateCalendarFeedToken: async () => {
    const response = await api.post(`${API_BASE}/users/me/calendar-feed/generate-token`);
    return response.data;
  },

  /**
   * Get calendar feed URL for current user
   */
  getCalendarFeedUrl: (token) => {
    return `${API_URL}/api/v2/calendar-feed/${token}`;
  },

  /**
   * Get webcal URL (for calendar apps)
   */
  getWebcalUrl: (token) => {
    const httpUrl = `${API_URL}/api/v2/calendar-feed/${token}`;
    // Replace http:// or https:// with webcal://
    return httpUrl.replace(/^https?:\/\//, 'webcal://');
  },
};

export default eventAPI;
